/**
 * Authentication API Service for Nour Al-Arabia
 *
 * Provides typed methods for all authentication-related API calls:
 * - Login with username/password
 * - Token refresh
 * - Logout
 * - Get current user info
 */

import { post, get } from './api';
import type {
  LoginRequest,
  LoginResponse,
  RefreshResponse,
  UserInfo,
} from '@/types/api';

// =============================================================================
// API Endpoints
// =============================================================================

const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  ME: '/auth/me',
} as const;

// =============================================================================
// Authentication API Methods
// =============================================================================

/**
 * Login with username and password
 *
 * @param credentials - Username and password
 * @returns Login response with access token and user info
 *
 * @example
 * const { accessToken, user } = await authApi.login({
 *   username: 'teacher1',
 *   password: 'teacher123'
 * });
 */
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  return post<LoginResponse, LoginRequest>(AUTH_ENDPOINTS.LOGIN, credentials);
}

/**
 * Refresh the access token using the httpOnly refresh cookie
 *
 * @returns New access token
 *
 * @example
 * const { accessToken } = await authApi.refresh();
 */
export async function refresh(): Promise<RefreshResponse> {
  return post<RefreshResponse>(AUTH_ENDPOINTS.REFRESH);
}

/**
 * Logout the current user
 *
 * This invalidates the session and clears the refresh cookie server-side.
 *
 * @example
 * await authApi.logout();
 */
export async function logout(): Promise<void> {
  return post<void>(AUTH_ENDPOINTS.LOGOUT);
}

/**
 * Get the current authenticated user's information
 *
 * @returns Current user info
 *
 * @example
 * const user = await authApi.getCurrentUser();
 * console.log(user.role); // 'TEACHER'
 */
export async function getCurrentUser(): Promise<UserInfo> {
  return get<UserInfo>(AUTH_ENDPOINTS.ME);
}

// =============================================================================
// Auth API Object Export
// =============================================================================

/**
 * Auth API service object with all authentication methods
 */
export const authApi = {
  login,
  refresh,
  logout,
  getCurrentUser,
} as const;

export default authApi;
